import React, { useCallback, useState } from "react";
import Select from "react-dropdown-select";
import styled from "@emotion/styled";
import { THEME_COLORS, FONT_SIZE, OTHER_CONSTANTS, FONT_FAMILY } from "../../themes";
import { Image } from "react-native-elements";
import { TouchableOpacity, View, StyleProp, TextStyle, FlatList } from "react-native";
import Text from "../CustomTextComponent";
import { styles } from "./styles";
import AwesomeDebouncePromise from "awesome-debounce-promise";

export type SelectInputVarsType = {
  labelField: string;
  valueField: string;
  searchBy?: string;
  fieldName: string;
  options: object[];
};
type ICustomSelectProps = {
  multi?: boolean;
  level?: string;
  hideSelectField?: string;
  labelStyles?: any;
  isRequired?: boolean;
  disabled?: boolean;
  values: object[];
  isLabelVisible?: boolean;
  filterObj?: any;
  filterHandler?: (data, search, obj) => any;
  type?: "select" | "filter";
  pagingInfo?: { numPages: number; currentPage: number };
  onChange: (value) => void;
  onSearch?: (searchInput: string) => void;
  closeOnSelect?: boolean;
  isClearVisible?: boolean;
  width?: number;
  height?: number;
  backgroundColor?: string;
  virtualized?: boolean;
  rowHeight?: number;
  header?: any;
  renderItem?: (data: { option: object; isCheckboxVisible: boolean }) => JSX.Element;
  keyField?: string;
  style?: any;
  placeholderTxtStyle?: StyleProp<TextStyle>;
  loading?: boolean;
  error?: any;
  refetch?: (arg: { page: number; perPage?: number; search: string }) => Promise<any>;
  fetchMore?: (arg: {
    variables: { page: number; perPage?: number };
    updateQuery: (prev: any, arg: any) => any;
  }) => Promise<any>;
  extraDataFrom: string;
} & SelectInputVarsType;

export const CustomPaginatedSelect = (props: ICustomSelectProps) => {
  const { loading, error, fetchMore, refetch, extraDataFrom } = props;
  const { currentPage = 1, numPages = 1 } = props.pagingInfo || {};
  const isLabelVisible = !!props.isLabelVisible ?? false;
  const type = !!props.type ? props.type : "select";
  const isClearVisible = type === "filter" || (!!props.isClearVisible ? props.isClearVisible : false);
  const header = !!props.header ? props.header : null;
  const placeholderTxtStyle = props.placeholderTxtStyle ?? {};
  const [initialLoading, setInitialLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const flatListRef = React.useRef();

  const selectStyles =
    type === "filter"
      ? { border: 0 }
      : isLabelVisible
        ? {
            minHeight: "auto",
            paddingVertical: 5,
            border: "none",
          }
        : {
            minHeight: "auto",
            boxShadow: `0 0 10px 0 ${THEME_COLORS.shadow1}`,
            padding: 0,
            width: !!props.width ? props.width : "auto",
            marginTop: 5,
            marginBottom: 5,
            backgroundColor: THEME_COLORS.white,
            borderRadius: 10,
            borderColor: props.isRequired && !(props.values.length > 0) ? THEME_COLORS.red : THEME_COLORS.black2,
            ...(props.style || {}),
          };

  const renderItem = useCallback(({ item }, addItem) => {
    return (
      <TouchableOpacityG onPress={() => addItem(item)}>
        <Text style={styles.listItem}>{item[props.labelField]}</Text>
      </TouchableOpacityG>
    );
  }, []);

  const nextPageCall = () => {
    let extraDataKey = extraDataFrom ? extraDataFrom : "carListPaginated";
    if (currentPage != numPages && !pageLoading) {
      fetchMore({
        variables: {
          page: currentPage + 1,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            [extraDataKey]: {
              ...fetchMoreResult[extraDataKey],
              data: [...prev[extraDataKey].data, ...fetchMoreResult[extraDataKey].data],
            },
          };
        },
      }).then(() => setPageLoading(false));
    }
  };

  const debouncePaginatedFn = AwesomeDebouncePromise(nextPageCall, 400);

  const onEndReached = () => {
    if (currentPage != numPages) setPageLoading(true);
    debouncePaginatedFn();
  };

  const onSearchFn = (val: string) => {
    if (refetch && !loading) {
      //@ts-ignore
      flatListRef?.current?.scrollToOffset({ animated: false, offset: 0 });
      refetch({
        page: 1,
        search: val,
      }).then(() => setInitialLoading(false));
    }
  };

  const debounceSearchFn = AwesomeDebouncePromise(onSearchFn, 800);

  const customDropdownRenderer = ({ props, state, methods }) => {
    return (
      <View style={{ width: 250 }}>
        {header}
        <SearchAndToggle color={props.color}>
          <Buttons>
            {/* {props.multi && <Button className="select-all" style={{justifyContent: 'flex-end',}}
                                ref={(r) => {
                                if (r) r.onclick=()=> {
                                        let oldVals = state.values.map(item => item[props.valueField]);
                                        let filteredNewData = filteredData.filter(item => !oldVals.includes(item[props.valueField]));
                                        methods.selectAll([...state.values,...filteredNewData]);
                                    }
                                }
                                }
                                >
                                  Select All
                                </Button>} */}
            {props.values.length > 0 && isClearVisible ? (
              <Button
                className="clear"
                style={{ justifyContent: "flex-end" }}
                ref={(r) => {
                  if (r)
                    r.onclick = () => {
                      methods.clearAll();
                    };
                }}
                onClick={methods.clearAll}
              >
                Clear
              </Button>
            ) : (
              <></>
            )}
          </Buttons>
          <input
            style={{ border: "none", backgroundColor: THEME_COLORS.grey2 }}
            autoFocus={true}
            value={state.search}
            type="text"
            onChange={(v) => {
              methods.setSearch(v);
              debounceSearchFn(v.target.value);
            }}
            placeholder={"Search " + props.fieldName}
          />
        </SearchAndToggle>
        {initialLoading ? (
          <View style={{ width: "100%", alignItems: "center", paddingVertical: 30 }}>
            <Text>Loading...</Text>
          </View>
        ) : (
          <FlatList
            //@ts-ignore
            ref={flatListRef}
            style={{ maxHeight: "40vh" }}
            data={props.options}
            keyExtractor={(item) => item.id.toString()}
            renderItem={(props) => renderItem(props, methods.addItem)}
            onEndReached={onEndReached}
            onEndReachedThreshold={0.01}
            ListFooterComponent={() =>
              pageLoading ? <Text style={{ textAlign: "center", marginVertical: 10 }}>Loading...</Text> : null
            }
            ListEmptyComponent={() => <Text>{error ? "Error fetching data" : "No data available yet"}</Text>}
          />
        )}
      </View>
    );
  };

  const className = "css-1ujv2ml-ReactDropdownSelect";
  let element = document.getElementById(className);
  if (!!!element) {
    element = document.createElement("div");
    element.setAttribute("id", className);
    element.setAttribute("class", className);
    element.setAttribute("style", "width: 0%; padding: 0px; z-index: 99999 !important");
    document.body.appendChild(element);
  }
  let closeOnSelect = true;
  if (!!props.multi && props.multi) {
    closeOnSelect = false;
  } else if (!!props.closeOnSelect) {
    closeOnSelect = props.closeOnSelect;
  }

  const renderStyledSelect = () => (
    <StyledSelect
      labelField={props.labelField}
      valueField={props.valueField}
      dropdownPosition="auto"
      values={props.values}
      onDropdownOpen={() => {
        setInitialLoading(true);
        onSearchFn("");
      }}
      options={props.options}
      rowHeight={props.rowHeight}
      onChange={props.onChange}
      multi={props.multi}
      closeOnSelect={closeOnSelect}
      disabled={props.disabled}
      dropdownHeight={"400px"}
      dropdownHandle={type !== "filter"}
      color={THEME_COLORS.primary}
      style={selectStyles}
      searchFn={onSearchFn}
      clearOnSelect={false}
      fieldName={props.fieldName}
      contentRenderer={({ props, state }) => {
        const values = props.values;
        const valuesLength = values.length;
        const isValueSelected = valuesLength > 0;
        let showValues = values;
        const maxVisibleValues = 2;
        if (isValueSelected && valuesLength > maxVisibleValues) {
          showValues = values.slice(0, maxVisibleValues);
        }
        const extraLength = valuesLength - maxVisibleValues;
        const showText = isValueSelected
          ? `${showValues.map((item) => item[props.labelField]).join(", ")}`
          : "Select " + props.fieldName;
        return type === "filter" ? (
          isValueSelected ? (
            <Image source={require("../../../assets/filterIcon.ico")} style={{ width: 15, height: 12 }} />
          ) : (
            <Image source={require("../../../assets/filter_icon.png")} style={{ width: 15, height: 12 }} />
          )
        ) : (
          <>
            <Text style={placeholderTxtStyle} numberOfLines={2} fontSize={"medium"} type_of_six={"three_six"}>
              {showText}
            </Text>
            {extraLength > 0 ? (
              <Text fontSize={"small"} type_of_six={"two_six"} style={{ color: THEME_COLORS.success }}>
                {" "}
                + {extraLength} more
              </Text>
            ) : null}
          </>
        );
      }}
      portal={typeof document !== `undefined` && element}
      dropdownRenderer={customDropdownRenderer}
    />
  );

  return !isLabelVisible ? (
    renderStyledSelect()
  ) : (
    <View
      style={{
        width: !!props.width ? props.width : 250,
        backgroundColor: !!props.backgroundColor ? props.backgroundColor : THEME_COLORS.grey2,
        height: 70,
        paddingHorizontal: 10,
        borderRadius: 8,
        marginLeft: 10,
        marginVertical: 10,
      }}
    >
      <Text style={[{ marginVertical: 5 }, props.labelStyles ?? {}]} fontSize={"medium"}>
        {props.label ?? ""}
      </Text>
      {renderStyledSelect()}
    </View>
  );
};

const StyledSelect = styled(Select)`
  .react-dropdown-select-dropdown {
    // position: absolute;
    // left: 0;
    // border: none;
    width: fit-content !important;
    // padding: 0;
    // display: flex;
    // flex-direction: column;
    // border-radius: 2px;
    // overflow: auto;
    z-index: 9999999 !important;
    // background: #333;
    //color: #fff !important;
  }
  .react-dropdown-select-content {
    font-size: ${FONT_SIZE.s2};
    padding-left: 5px;
    align-content: center;
    min-height: ${OTHER_CONSTANTS.selectInputHeight};
    font-family: ${FONT_FAMILY.primary};
  }
`;

const SearchAndToggle = styled.div`
  display: flex;
  flex-direction: column;
  input {
    margin: 10px 10px 0;
    line-height: 30px;
    padding: 0px 20px;
    border: 1px solid #ccc;
    border-radius: 3px;
    :focus {
      outline: none;
      border: 1px solid ${THEME_COLORS.primary};
    }
  }
`;

const TouchableOpacityG = styled(TouchableOpacity)`
  :hover {
    ${({ disabled }) => !disabled && "background: #f2f2f2; "}
  }
  // ${({ disabled }) => disabled && "text-decoration: line-through;"}
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  & div {
    margin: 10px 0 0 10px;
    font-weight: 600;
  }
`;

const Button = styled.button`
  background: none;
  border: 1px solid #555;
  color: #555;
  border-radius: 3px;
  margin: 10px 10px 0;
  padding: 3px 5px;
  font-size: 10px;
  text-transform: uppercase;
  cursor: pointer;
  outline: none;
  &.clear {
    color: tomato;
    border: 1px solid tomato;
  }
  &.select-all {
    color: green;
    border: 1px solid green;
  }
  :hover {
    border: 1px solid deepskyblue;
    color: deepskyblue;
  }
`;

const DropDown = styled.div`
  .css-1snwlti-DropDown {
    min-width: 100;
  }
`;
